import { ActionsUnion, createAction } from '@housinganywhere/safe-redux';
import reportError from 'ha/helpers/bugsnag/reportError';
import HttpStatus from 'ha/constants/HttpStatus';
import apiV2ErrorTranslations from 'ha/constants/apiV2ErrorTranslations';
import { removeFromStorage } from 'ha/utils/storage';
import { MakeCallPromise } from 'ha/services/apiV2/createMakeCall';
import { Thunk } from 'ha/myredux/types';
import { UserApiV2 } from 'ha/services/apiV2/types/User';
import {
  ImpersonationInfo,
  MyUserInfoApiV2,
} from 'ha/services/apiV2/types/UserInfo';
import { Payload as LoginPayload } from 'ha/services/apiV2/login';
import { Payload as RegisterPayload } from 'ha/services/apiV2/register';
import {
  BILLIN_INFO_STORAGE_KEY,
  SHOW_WE_WANT_TO_PAY_STORAGE_KEY,
} from 'ha/modules/WeWantToPay/constants';
import { AuthLogicTypes } from 'ha/modules/AuthLogic/constants';
import { RequestError } from 'ha/services/apiV2';
import { AuthType } from '../types';
import fetchUserBillingInfo from './fetchUserBillingInfo';

export const AuthLogicActions = {
  authStart: () => createAction(AuthLogicTypes.authStart),
  authDone: () => createAction(AuthLogicTypes.authDone),
  authFail: () => createAction(AuthLogicTypes.authFail),
  login: (
    user: UserApiV2,
    userInformation: MyUserInfoApiV2,
    dialogType: AuthType,
  ) =>
    createAction(
      AuthLogicTypes.login,
      { user, userInformation },
      { analytics: { type: dialogType, payload: { user } } },
    ),
  logout: () =>
    createAction(AuthLogicTypes.logout, undefined, {
      analytics: { type: 'logout' },
    }),
  updateUser: (payload: UserApiV2) =>
    createAction(AuthLogicTypes.updateUser, payload),
  initUser: (payload: { user: UserApiV2; userInformation: MyUserInfoApiV2 }) =>
    createAction(AuthLogicTypes.initUser, payload),
  fulfillImpersonation: (payload: ImpersonationInfo) =>
    createAction(AuthLogicTypes.fulfillImpersonation, payload),
};
export type AuthLogicActions = ActionsUnion<typeof AuthLogicActions>;

export const updateUserData = AuthLogicActions.updateUser;

export const login: (user: UserApiV2, dialogType: AuthType) => Thunk = (
  user,
  dialogType,
) => (dispatch, getState, services) => {
  return services.apiV2
    .getUserInformation('me', {
      expand: [
        'advertiserBookingsCount',
        'savedPaymentMethodsCount',
        'listingsCount',
      ].join(','),
    })
    .then(({ data: userInformation }) => {
      dispatch(AuthLogicActions.login(user, userInformation, dialogType));
      removeFromStorage(BILLIN_INFO_STORAGE_KEY);
      removeFromStorage(SHOW_WE_WANT_TO_PAY_STORAGE_KEY);
      // do not return this to avoid making form submission invalid if
      // fetching user information fails (which is just to update banner)
      dispatch(fetchUserBillingInfo());
    });
};

type LogoutReturn = MakeCallPromise<null>;
type LogoutAfter = (data: { data: null; status: number }) => void;

export const logout: (
  after?: LogoutAfter,
) => Thunk<{}, LogoutReturn> = after => (dispatch, _, { apiV2 }) => {
  dispatch(AuthLogicActions.logout());

  removeFromStorage(BILLIN_INFO_STORAGE_KEY);
  removeFromStorage(SHOW_WE_WANT_TO_PAY_STORAGE_KEY);

  // This promise is captured here and then returned below.
  // TypeScript gets confused by the type of the return here,
  // for some reason it thinks the type is `typeof data`
  // instead of Promise<typeof data> when returning directly.
  // Sadface.
  const resolvedPromise: LogoutReturn = apiV2.logout().then(data => {
    if (after) {
      after(data);
    }
    return data;
  });

  return resolvedPromise;
};

type LoginRequest = (fields: LoginPayload) => Thunk;

export const loginRequest: LoginRequest = fields => (
  dispatch,
  getState,
  services,
) => {
  dispatch(AuthLogicActions.authStart());

  return services.apiV2
    .login(fields)
    .then(({ data }) => {
      dispatch(AuthLogicActions.authDone());

      return dispatch(login(data, AuthType.login));
    })
    .catch((err: RequestError) => {
      dispatch(AuthLogicActions.authFail());

      if (err.status === HttpStatus.FORBIDDEN) {
        throw new Error(apiV2ErrorTranslations.emailOrPasswordIncorrect);
      } else if (err.status === HttpStatus.SERVICE_UNAVAILABLE) {
        throw new Error(apiV2ErrorTranslations.maxAttemptsReached);
      }

      throw err;
    });
};

type RegisterRequest = (fields: RegisterPayload) => Thunk;

export const registerRequest: RegisterRequest = fields => (
  dispatch,
  getState,
  services,
) => {
  dispatch(AuthLogicActions.authStart());

  return services.apiV2
    .register(fields)
    .then(({ data }) => {
      dispatch(AuthLogicActions.authDone());

      return dispatch(login(data, AuthType.register));
    })
    .catch((err: RequestError) => {
      dispatch(AuthLogicActions.authFail());

      throw err;
    });
};

export const fetchUser: () => Thunk = () => (dispatch, getState, services) =>
  Promise.all([
    services.apiV2.getUser('me'),
    services.apiV2.getUserInformation('me', {
      expand: [
        'advertiserBookingsCount',
        'savedPaymentMethodsCount',
        'listingsCount',
      ].join(','),
    }),
  ])
    .then(([{ data: user }, { data: userInformation }]) => {
      dispatch(
        AuthLogicActions.initUser({
          user,
          userInformation,
        }),
      );
    })
    .catch((error: RequestError) => {
      dispatch(AuthLogicActions.authDone());

      if (error.status === 401) {
        return;
      }

      reportError(error);
    });

export const fetchImpersonationInfo: () => Thunk = () => (
  dispatch,
  _,
  { apiV2 },
) =>
  apiV2
    .getUserInformation('me', {
      expand: 'impersonation',
    })
    .then(({ data: { impersonation } }) => {
      if (impersonation) {
        dispatch(AuthLogicActions.fulfillImpersonation(impersonation));

        removeFromStorage(BILLIN_INFO_STORAGE_KEY);
        removeFromStorage(SHOW_WE_WANT_TO_PAY_STORAGE_KEY);
      }
    })
    .catch(error => {
      if (!error.cause || error.cause.status !== HttpStatus.UNAUTHORIZED) {
        reportError('fetchImpersonationInfo failed', { metaData: { error } });
      }
    });
